import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import type { AudienceSummary, Campaign, LeadList, SequenceSummary } from '@/api/types';
import { useFeatureFlags } from '@/features/platform/useFeatureFlags';
import { CAMPAIGN_TYPE_LABELS, CAMPAIGN_TYPES } from '@marketing-hub/shared';
import { useProjectContext } from '@/project/ProjectContext';
import { Button } from '@/components/ui/Button';
import { Field, TextInput } from '@/components/ui/Field';
import { LoadingState } from '@/components/StateMessage/StateMessage';
import { CampaignReadinessPanel } from '@/features/campaigns/CampaignReadinessPanel';
import { CampaignSimulatePanel } from '@/features/campaigns/CampaignSimulatePanel';
import campStyles from '@/features/campaigns/Campaigns.module.css';
import leadStyles from '@/features/leads/Leads.module.css';
import styles from '../ProjectsPage.module.css';

const STEPS = ['Intent', 'Audience', 'Sequence', 'Preview', 'Review'] as const;

export function CampaignWizardPage() {
  const navigate = useNavigate();
  const { selectedProject } = useProjectContext();
  const projectId = selectedProject?.id;
  const qc = useQueryClient();
  const [step, setStep] = useState(0);

  const [campaignName, setCampaignName] = useState('');
  const [campaignType, setCampaignType] =
    useState<(typeof CAMPAIGN_TYPES)[number]>('cold_outreach');
  const [pickListId, setPickListId] = useState('');
  const [pickAudienceId, setPickAudienceId] = useState('');
  const [pickSeqId, setPickSeqId] = useState('');
  const { isEnabled } = useFeatureFlags();
  const audiencesOn = isEnabled('audiences_v1');

  const listsQ = useQuery({
    queryKey: ['lists', projectId],
    enabled: Boolean(projectId),
    queryFn: () => apiFetch<{ lists: LeadList[] }>(`/api/lists?project_id=${projectId}`),
  });

  const audiencesQ = useQuery({
    queryKey: ['audiences', projectId],
    enabled: Boolean(projectId) && audiencesOn,
    queryFn: () =>
      apiFetch<{ audiences: AudienceSummary[] }>(`/api/audiences?project_id=${projectId}`),
  });

  const seqQ = useQuery({
    queryKey: ['sequences', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{ sequences: SequenceSummary[] }>(`/api/sequences?project_id=${projectId}`),
  });

  const seqDetailQ = useQuery({
    queryKey: ['sequence-detail', pickSeqId],
    enabled: Boolean(pickSeqId),
    queryFn: async () => {
      const res = await apiFetch<{
        steps: {
          step_order: number;
          subject_template: string;
          email_template_id: string | null;
        }[];
      }>(`/api/sequences/${pickSeqId}`);
      return res;
    },
  });

  const templatesQ = useQuery({
    queryKey: ['email-templates', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{ templates: { id: string; name: string; template_version: number }[] }>(
        `/api/email-templates?project_id=${projectId}`,
      ),
  });

  const createCampaign = useMutation({
    mutationFn: () =>
      apiFetch<{ campaign: Campaign }>('/api/campaigns', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          name: campaignName,
          type: campaignType,
          ...(pickAudienceId
            ? { audience_id: pickAudienceId }
            : { list_id: pickListId }),
          sequence_id: pickSeqId,
        }),
      }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['campaigns', projectId] });
      navigate(`/app/campagnes/${data.campaign.id}`);
    },
  });

  if (!projectId) return null;
  if (listsQ.isLoading || seqQ.isLoading) return <LoadingState />;

  const canNext =
    (step === 0 && campaignName.trim().length > 0) ||
    (step === 1 && (pickListId || pickAudienceId)) ||
    (step === 2 && pickSeqId) ||
    step === 3 ||
    step === 4;

  return (
    <div className={campStyles.wizardLayout}>
      <div>
        <nav className={campStyles.wizardStepper} aria-label="Wizard stappen">
          {STEPS.map((label, i) => (
            <div
              key={label}
              className={[
                campStyles.wizardStepItem,
                i === step ? campStyles.wizardStepActive : '',
                i < step ? campStyles.wizardStepDone : '',
              ].join(' ')}
            >
              <span className={campStyles.wizardStepNum}>{i < step ? '✓' : i + 1}</span>
              {label}
            </div>
          ))}
        </nav>

        {step === 0 ? (
          <section className={campStyles.wizardCard}>
            <h2 className={campStyles.wizardCardTitle}>Intent & type</h2>
            <div className={styles.formGrid}>
              <Field label="Campagnenaam (conventie: Project | type | slug)">
                <TextInput
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder="Ploko | nurture | inbound-30d"
                  required
                />
              </Field>
              <Field label="Type">
                <select
                  className={leadStyles.filterInput}
                  value={campaignType}
                  onChange={(e) =>
                    setCampaignType(e.target.value as (typeof CAMPAIGN_TYPES)[number])
                  }
                >
                  {CAMPAIGN_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {CAMPAIGN_TYPE_LABELS[t]}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </section>
        ) : null}

        {step === 1 ? (
          <section className={campStyles.wizardCard}>
            <h2 className={campStyles.wizardCardTitle}>Audience</h2>
            {audiencesOn ? (
              <Field label="Doelgroep (aanbevolen)">
                <select
                  className={leadStyles.filterInput}
                  value={pickAudienceId}
                  onChange={(e) => {
                    setPickAudienceId(e.target.value);
                    if (e.target.value) setPickListId('');
                  }}
                >
                  <option value="">— kies doelgroep —</option>
                  {(audiencesQ.data?.audiences ?? []).map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} ({a.lead_count} contacten)
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
            <Field label={audiencesOn ? 'Of: losse lijst' : 'Lijst'}>
              <select
                className={leadStyles.filterInput}
                value={pickListId}
                onChange={(e) => {
                  setPickListId(e.target.value);
                  if (e.target.value) setPickAudienceId('');
                }}
                disabled={Boolean(pickAudienceId)}
              >
                <option value="">— kies —</option>
                {(listsQ.data?.lists ?? []).map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name} ({l.member_count})
                  </option>
                ))}
              </select>
            </Field>
            <p className={styles.mutedSmall}>
              {audiencesOn ? (
                <>
                  <Link to="/app/doelgroepen">Doelgroepen beheren</Link>
                  {' · '}
                </>
              ) : null}
              <Link to="/app/campagnes/bibliotheek">Bibliotheek → lijst aanmaken</Link>
            </p>
          </section>
        ) : null}

        {step === 2 ? (
          <section className={campStyles.wizardCard}>
            <h2 className={campStyles.wizardCardTitle}>Sequence</h2>
            <Field label="Sequence">
              <select
                className={leadStyles.filterInput}
                value={pickSeqId}
                onChange={(e) => setPickSeqId(e.target.value)}
              >
                <option value="">— kies —</option>
                {(seqQ.data?.sequences ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </Field>
            <p className={styles.mutedSmall}>
              Broadcast = 1 step in sequence.{' '}
              <Link to="/app/campagnes/bibliotheek">Nieuwe sequence in bibliotheek</Link>
            </p>
            {pickSeqId && (seqDetailQ.data?.steps?.length ?? 0) > 0 ? (
              <ul className={styles.list}>
                {seqDetailQ.data!.steps.map((st) => {
                  const tpl = st.email_template_id
                    ? (templatesQ.data?.templates ?? []).find((t) => t.id === st.email_template_id)
                    : null;
                  return (
                    <li key={st.step_order} className={styles.mutedSmall}>
                      Stap {st.step_order}: {tpl ? `template ${tpl.name} v${tpl.template_version}` : 'inline tekst'}{' '}
                      — {st.subject_template.slice(0, 60)}
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </section>
        ) : null}

        {step === 3 ? (
          <section className={campStyles.wizardCard}>
            <h2 className={campStyles.wizardCardTitle}>Preview & simulate</h2>
            <p className={styles.mutedSmall}>
              Controleer merge-variabelen vóór aanmaken. Koppel templates in de bibliotheek (sequence
              stap 1).
            </p>
            {pickSeqId ? (
              <CampaignSimulatePanel projectId={projectId} sequenceId={pickSeqId} />
            ) : (
              <p className={styles.mutedSmall}>Kies eerst een sequence.</p>
            )}
          </section>
        ) : null}

        {step === 4 ? (
          <section className={campStyles.wizardCard}>
            <h2 className={campStyles.wizardCardTitle}>Review & aanmaken</h2>
            <ul className={styles.list}>
              <li className={styles.mutedSmall}>
                <strong>{campaignName}</strong> · {CAMPAIGN_TYPE_LABELS[campaignType]}
              </li>
              <li className={styles.mutedSmall}>
                Audience:{' '}
                {pickAudienceId
                  ? (audiencesQ.data?.audiences ?? []).find((a) => a.id === pickAudienceId)?.name ??
                    pickAudienceId
                  : (listsQ.data?.lists ?? []).find((l) => l.id === pickListId)?.name ?? pickListId}
              </li>
              <li className={styles.mutedSmall}>
                Sequence:{' '}
                {(seqQ.data?.sequences ?? []).find((s) => s.id === pickSeqId)?.name ?? pickSeqId}
              </li>
            </ul>
            <form
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                createCampaign.mutate();
              }}
            >
              <Button type="submit" disabled={createCampaign.isPending}>
                Campagne aanmaken
              </Button>
            </form>
          </section>
        ) : null}

        <div className={campStyles.wizardNav}>
          <Button
            type="button"
            variant="ghost"
            disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
          >
            Terug
          </Button>
          {step < 4 ? (
            <Button
              type="button"
              disabled={!canNext}
              onClick={() => setStep((s) => Math.min(4, s + 1))}
            >
              Volgende stap
            </Button>
          ) : null}
        </div>
      </div>

      <CampaignReadinessPanel projectId={projectId} />
    </div>
  );
}
